import angular from 'angular';
import { filter, forEach, indexOf, remove, sortBy } from 'lodash';
import '../../components/osom';
import '../../components/permission';
import '../../components/queue';
import '../../services/area';
import '../../services/auth';
import '../../services/errors';
import '../../services/line';
import '../../services/place';
import '../../services/resource-group';
import '../../services/user';

const module = angular.module('whyline.modals.pending-user', [
  'whyline.components.osom',
  'whyline.components.queue',
  'whyline.components.permission',
  'whyline.services.place',
  'whyline.services.line',
  'whyline.services.area',
  'whyline.services.user',
  'whyline.services.resource-group',
  'whyline.services.auth',
  'whyline.services.errors',
]);

const OrganizationPendingUserModalController = function(
  $uibModalInstance,
  $translate,
  NotificationService,
  UserService,
  ErrorService,
  userId,
  roles,
  organizationId,
  placeId,
  $timeout,
  ResourceGroupService,
  PlaceService,
  blockUI,
) {
  'ngInject';

  // Initialization
  this.currentUser = UserService.GetCurrent();
  this.placeId = placeId;
  this.globalUserPermission = '';
  this.alreadyShown = {};
  this.user = {
    profile: {},
    local: {},
    permissions: [],
  };
  this.roles = sortBy(roles, 'name');
  this.boxLabelRequired = PlaceService.GetCurrent().configuration.boxLabelRequired;

  if (userId) {
    UserService.GetOneAsPromiseFromServer(userId)
      .then((user) => {
        $timeout(() => {
          this.user = UserService.Copy(user);
          this.globalUserPermission = UserService.GetCurrentRoleFor(user);
          generateAlreadyShown();
        });
      })
      .catch(ErrorService.handler);
  }

  let validate = () => {
    const firstName = this.user.profile.firstName;
    const lastName = this.user.profile.lastName;

    if (!firstName) {
      NotificationService.Warning($translate.instant('mandatory_user_name'));
      return false;
    }

    if (firstName.length < 2 || firstName.length > 512) {
      NotificationService.Warning($translate.instant('user_firstname_length'));
      return false;
    }

    if (!lastName) {
      NotificationService.Warning($translate.instant('mandatory_user_lastname'));
      return false;
    }

    if (lastName.length < 2 || lastName.length > 512) {
      NotificationService.Warning($translate.instant('user_lastname_length'));
      return false;
    }

    if (!this.globalUserPermission) {
      NotificationService.Warning('Debes seleccionar un permiso global para el usuario.');
      return false;
    }

    if (this.boxLabelRequired) {
      const name = this.queue ? this.queue.label : this.label;

      const msjValidations = this.user.permissions
        .filter(
          (permission) =>
            permission.boxLabelEnabled &&
            permission.boxLabelVisible &&
            (!permission.boxLabel || !permission.boxLabel.trim()),
        )
        .map((permission) =>
          $translate
            .instant('{name}_box_label_required')
            .replace('{name}', permission.resourceGroup.name.toUpperCase()),
        );

      if (msjValidations && msjValidations.length > 0) {
        msjValidations.forEach((msj) => NotificationService.Warning(msj));
        return false;
      }
    }

    return true;
  };

  this.save = () => {
    if (validate()) {
      this.checkPlacePermission().then(() => {
        this.user.permissions = this.depopulatePermissions(this.user.permissions);

        const query = 'external=true';
        const userBlockUI = blockUI.instances.get('user-block-ui');

        userBlockUI.start();

        if (userId) {
          UserService.Update(this.user, query)
            .then(() => {
              NotificationService.Success(
                $translate
                  .instant('user_{name}_update_succ')
                  .replace('{name}', this.user.profile.firstName),
              );
            })
            .catch(() => {
              const text = $translate.instant('unable_user_save');
              NotificationService.Warning(text);
            })
            .finally(() => {
              $uibModalInstance.close('cancel');
              userBlockUI.stop();
            });
        } else {
          this.user.profile.language = PlaceService.GetCurrent().language;

          UserService.Create(this.user, query)
            .then(() => {
              NotificationService.Success(
                `Usuario ${this.user.profile.firstName} creado correctamente.`,
              );
            })
            .catch(() => {
              NotificationService.Warning(
                `No se pudo crear el usuario ${this.user.profile.firstName} ${this.user.profile.lastName}`,
              );
            })
            .finally(() => {
              $uibModalInstance.close('cancel');
              userBlockUI.stop();
            });
        }
      });
    }
  };

  this.selectResourceGroup = (resourceGroup) => {
    this.user.permissions.push({
      placeId: this.placeId,
      resourceGroup,
    });
    generateAlreadyShown();
  };

  const removeIfExists = (permission, permissions) => {
    const index = indexOf(permissions, permission);
    permissions.splice(index, 1);
  };

  this.removePermission = (permission) => {
    removeIfExists(permission, this.user.permissions);
    generateAlreadyShown();
  };

  this.cancel = () => {
    $uibModalInstance.close('cancel');
  };

  this.depopulatePermissions = function(permissions) {
    return permissions.map((permission) => ({
      placeId: permission.placeId,
      resourceGroup: permission.resourceGroup._id,
      role: permission.role ? permission.role._id : this.globalUserPermission._id,
      boxLabel: permission.boxLabelEnabled && permission.boxLabelVisible ? permission.boxLabel : '',
    }));
  };

  this.checkPlacePermission = () => {
    // If global permission is not operator
    if (this.placeId && this.globalUserPermission.name === 'Statistical') {
      this.user.permissions = this.user.permissions.filter((permission) => {
        const isPlaceType = permission.resourceGroup.type === 'place';
        const isFromThisPlace = permission.resourceGroup.placeId === this.placeId;
        return !isFromThisPlace || (isFromThisPlace && isPlaceType);
      });
    }

    if (this.placeId && this.globalUserPermission.name !== 'operator') {
      let placePermissionFound = false;
      // Look over all user's permissions
      this.user.permissions.forEach((permission) => {
        if (
          permission.resourceGroup.type === 'place' &&
          permission.resourceGroup.placeId === this.placeId
        ) {
          permission.role = this.globalUserPermission;
          placePermissionFound = true;
        }
      });

      if (!placePermissionFound) {
        return ResourceGroupService.GetAllAsPromise()
          .then((resources) =>
            $timeout(() => {
              const PlaceResourceGroup = filter(resources, {
                type: 'place',
                placeId: this.placeId,
              });
              this.user.permissions.push({
                placeId: this.placeId,
                resourceGroup: PlaceResourceGroup[0],
                role: this.globalUserPermission,
                boxLabel: '',
              });
              return;
            }),
          )
          .catch(ErrorService.handler);
      } else {
        return Promise.resolve();
      }
    } else if (this.placeId && this.globalUserPermission.name === 'operator') {
      // Look over all user's permissions
      remove(
        this.user.permissions,
        (permission) =>
          permission.resourceGroup.type === 'place' &&
          permission.resourceGroup.placeId === this.placeId,
      );
      return Promise.resolve();
    } else {
      return Promise.resolve();
    }
  };

  const generateAlreadyShown = () => {
    this.alreadyShown = {};
    forEach(this.user.permissions, (permission) => {
      if (permission.resourceGroup) {
        this.alreadyShown[permission.resourceGroup._id] = permission.resourceGroup;
      }
    });
  };
};

module.exports = module.controller(
  'OrganizationPendingUserModalController',
  OrganizationPendingUserModalController,
);
